/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * Feedback Detail Modal - View individual feedback details
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import React from 'react'
import { X, Calendar, Image as ImageIcon, CheckCircle } from 'lucide-react'
import ModalPortal from '../ModalPortal'
import useFeedbackStore from '../../stores/feedbackStore'
import FeedbackCategoryBadge from './FeedbackCategoryBadge'
import FeedbackStatusBadge from './FeedbackStatusBadge'
import FeedbackRatingDisplay from './FeedbackRatingDisplay'

const FeedbackDetailModal = () => {
  const { isDetailModalOpen, selectedFeedback, closeDetailModal } = useFeedbackStore()

  if (!isDetailModalOpen || !selectedFeedback) return null

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <ModalPortal>
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black bg-opacity-50" onClick={closeDetailModal} />

        {/* Modal */}
        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
              Feedback Details
            </h2>
            <button
              onClick={closeDetailModal}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
              aria-label="Close"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Badges and Rating */}
            <div className="flex flex-wrap items-center gap-3">
              <FeedbackCategoryBadge category={selectedFeedback.category} />
              <FeedbackStatusBadge status={selectedFeedback.status} />
              {selectedFeedback.screenshot && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600">
                  <ImageIcon className="w-4 h-4" />
                  Screenshot Attached
                </span>
              )}
            </div>

            {selectedFeedback.rating && (
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Rating
                </h3>
                <FeedbackRatingDisplay rating={selectedFeedback.rating} showNumber />
              </div>
            )}

            {/* Message */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Your Feedback
              </h3>
              <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <p className="text-gray-900 dark:text-gray-100 whitespace-pre-wrap">
                  {selectedFeedback.message}
                </p>
              </div>
            </div>

            {/* Screenshot */}
            {selectedFeedback.screenshot && (
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Screenshot
                </h3>
                <div className="rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden">
                  <a
                    href={selectedFeedback.screenshot}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block hover:opacity-90 transition-opacity"
                  >
                    <img
                      src={selectedFeedback.screenshot}
                      alt="Feedback screenshot"
                      className="w-full h-auto cursor-pointer"
                      onError={(e) => {
                        e.target.onerror = null // Prevent infinite loop
                        e.target.src =
                          'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgZmlsbD0iI2Y1ZjVmNSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTYiIGZpbGw9IiM5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5TY3JlZW5zaG90IG5vdCBhdmFpbGFibGU8L3RleHQ+PC9zdmc+'
                      }}
                    />
                  </a>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 px-2 pb-2">
                    Click to open in new tab
                  </p>
                </div>
              </div>
            )}

            {/* Context Information */}
            {(selectedFeedback.challenge ||
              selectedFeedback.stage ||
              selectedFeedback.question) && (
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Context
                </h3>
                <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg space-y-2 text-sm">
                  {selectedFeedback.challenge && (
                    <div>
                      <span className="font-medium text-gray-700 dark:text-gray-300">
                        Challenge:{' '}
                      </span>
                      <span className="text-gray-900 dark:text-gray-100">
                        {selectedFeedback.challenge.name}
                      </span>
                    </div>
                  )}
                  {selectedFeedback.stage && (
                    <div>
                      <span className="font-medium text-gray-700 dark:text-gray-300">Stage: </span>
                      <span className="text-gray-900 dark:text-gray-100">
                        {selectedFeedback.stage.name}
                      </span>
                    </div>
                  )}
                  {selectedFeedback.question && (
                    <div>
                      <span className="font-medium text-gray-700 dark:text-gray-300">
                        Question ID:{' '}
                      </span>
                      <span className="text-gray-900 dark:text-gray-100">
                        {selectedFeedback.question.id}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Metadata */}
            {selectedFeedback.metadata && (
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Technical Metadata
                </h3>
                <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <pre className="text-xs text-gray-900 dark:text-gray-100 overflow-x-auto whitespace-pre-wrap break-words font-mono">
                    {JSON.stringify(selectedFeedback.metadata, null, 2)}
                  </pre>
                </div>
              </div>
            )}

            {/* Admin Response */}
            {selectedFeedback.adminResponse && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Admin Response
                  </h3>
                </div>
                <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <p className="text-gray-900 dark:text-gray-100 whitespace-pre-wrap mb-3">
                    {selectedFeedback.adminResponse}
                  </p>
                  {selectedFeedback.respondedAt && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      Responded on {formatDate(selectedFeedback.respondedAt)}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Metadata */}
            <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">Submitted: </span>
                  <span className="text-gray-900 dark:text-gray-100">
                    {formatDate(selectedFeedback.createdAt)}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">
                    Last Updated:{' '}
                  </span>
                  <span className="text-gray-900 dark:text-gray-100">
                    {formatDate(selectedFeedback.updatedAt)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-6 py-4">
            <button
              onClick={closeDetailModal}
              className="w-full px-4 py-3 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-gray-100 rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </ModalPortal>
  )
}

export default FeedbackDetailModal
